import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Check, Sparkles, Loader2, CheckCircle, XCircle, ExternalLink, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

export const PlanCard = ({
  plan,
  planKey,
  billingPeriod,
  formatPrice,
  isMyPlan,
  isActive,
  isOtherActive,
  canBuy,
  onCheckout,
  checkoutLoading,
  recommended,
  icon,
  accentColor,
  subDetails,
  onCancel,
  onReactivate,
  reactivateLoading,
  onPortal,
  portalLoading,
}) => {
  if (!plan) return null;
  const priceObj = plan[billingPeriod];
  const isHighlighted = recommended || isActive;
  const subIsCanceling = subDetails?.cancel_at_period_end;

  return (
    <Card
      className={cn("border-2 relative overflow-hidden transition-shadow duration-200", isHighlighted && "shadow-lg")}
      style={isHighlighted ? { borderColor: accentColor } : {}}
      data-testid={`plan-${planKey}`}
    >
      {recommended && !isActive && (
        <div className="absolute top-0 right-0 text-white px-4 py-1 text-[10px] font-semibold rounded-bl-lg" style={{ backgroundColor: accentColor }}>
          RECOMMANDÉ
        </div>
      )}
      {isActive && (
        <div className="absolute top-0 right-0 text-white px-4 py-1 text-[10px] font-semibold rounded-bl-lg bg-[var(--success)]">
          ACTIF
        </div>
      )}

      <CardHeader className="text-center pb-3 pt-6">
        <CardTitle className="text-xl flex items-center justify-center gap-2" style={{ color: accentColor }}>
          {icon}
          {plan.plan_name}
        </CardTitle>
        <CardDescription className="text-xs">{plan.tagline}</CardDescription>
        <div className="mt-4">
          <span className="text-4xl font-bold" style={{ color: accentColor }} data-testid={`price-${planKey}`}>
            {formatPrice(priceObj)}
          </span>
          <span className="text-sm text-[var(--text-muted)]">/{priceObj?.interval_label}</span>
        </div>
        {billingPeriod === 'annual' && (
          <p className="text-[10px] text-[var(--success)] mt-1">
            soit {(priceObj?.price_float / 12).toFixed(2).replace('.', ',')}€/mois
          </p>
        )}
      </CardHeader>

      <CardContent>
        <ul className="space-y-2.5">
          {(plan.features || []).map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--success)' }} />
              <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{f}</span>
            </li>
          ))}
        </ul>

        {plan.note && (
          <p className="text-[11px] italic mt-3 text-center" style={{ color: 'var(--text-muted)' }}>
            {plan.note}
          </p>
        )}

        <div className="mt-6">
          {isActive ? (
            <div className="space-y-3">
              <Button disabled className="w-full gap-2" variant="outline" data-testid={`active-${planKey}`}>
                <CheckCircle className="h-4 w-4" /> Abonnement actif
              </Button>

              {/* Management buttons inside the active plan card */}
              <Separator />
              <div className="flex flex-col gap-2">
                {subIsCanceling ? (
                  <Button
                    variant="outline"
                    className="w-full gap-2 border-[var(--success)] text-[var(--success)] hover:bg-[var(--success-light)]"
                    onClick={onReactivate}
                    disabled={reactivateLoading}
                    data-testid={`reactivate-${planKey}`}
                  >
                    {reactivateLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Réactiver mon abonnement
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full gap-2 text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-950/20"
                    onClick={onCancel}
                    data-testid={`cancel-${planKey}`}
                  >
                    <XCircle className="h-4 w-4" />
                    Résilier
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="w-full gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  onClick={onPortal}
                  disabled={portalLoading}
                  data-testid={`manage-${planKey}`}
                >
                  {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                  Gérer via Stripe
                </Button>
              </div>
            </div>
          ) : canBuy ? (
            <Button
              onClick={() => onCheckout(planKey)}
              disabled={checkoutLoading}
              className="w-full h-11 font-semibold gap-2 text-white"
              style={{ backgroundColor: accentColor }}
              data-testid={`checkout-${planKey}`}
            >
              {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {checkoutLoading ? 'Redirection…' : `Passer à ${plan.plan_name}`}
            </Button>
          ) : isOtherActive ? (
            <div className="text-center text-xs p-2 rounded bg-[var(--success-light)] text-[var(--success)]">
              <CheckCircle className="inline h-3.5 w-3.5 mr-1" />
              Inclus via votre partenaire
            </div>
          ) : !isMyPlan ? (
            <div className="text-center text-xs p-2 rounded bg-[var(--neutral-50)] text-[var(--text-muted)]">
              Réservé aux {planKey === 'parent_pro' ? 'parents' : 'nounous'}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};
